import axios from 'axios';

import { getEnvByHostname } from '../utils/env';

const api = axios.create({
  timeout: 30000,
  headers: {
    'appId': '111'
  },
  withCredentials: true // 支持Cookie
});

api.interceptors.request.use(config => {
  const operator = localStorage.getItem('username') || 'test_user';
  config.headers['X-Operator'] = operator;
  // Cookie会自动携带，不需要手动添加Token
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith('/api-docs')) {
      localStorage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const testApi = {
  executeTest: (data) => {
    console.log('testApi.executeTest called:', data);
    // 注入泳道标识（测试环境可选）
    const swimlane = localStorage.getItem('swimlane');
    if (swimlane) {
      data.swimlane = swimlane;
    }
    return api.post('/inner/v1/fiat-payment/test/execute', data);
  },
  queryAccount: (data) => api.post('/inner/v1/fiat-payment/test/query', data),
  getChannels: () => api.get('/inner/v1/fiat-payment/test/channels'),
  _channelCurrenciesCache: null,
  _channelCurrenciesPromise: null,
  getChannelCurrencies() {
    if (this._channelCurrenciesCache) {
      return Promise.resolve(this._channelCurrenciesCache);
    }
    if (this._channelCurrenciesPromise) {
      return this._channelCurrenciesPromise;
    }
    this._channelCurrenciesPromise = api.post('/inner/v1/fiat-payment/test/execute', {
      testType: 'CHANNEL_CURRENCIES',
      channelCode: '',
      businessPartner: '',
      currency: '',
      env: getEnvByHostname(),
      executor: localStorage.getItem('username') || 'admin',
      requestBody: '{}'
    }).then(res => {
      this._channelCurrenciesCache = res;
      this._channelCurrenciesPromise = null;
      return res;
    }).catch(err => {
      this._channelCurrenciesPromise = null;
      throw err;
    });
    return this._channelCurrenciesPromise;
  },
  invalidateChannelCurrenciesCache() {
    this._channelCurrenciesCache = null;
  },
  getExecutions: (params) => api.get('/inner/v1/fiat-payment/test/executions', { params }),
  getExecutionDetail: (executionId) => api.get(`/inner/v1/fiat-payment/test/executions/${executionId}`),
  getUserAccounts: (externalUserId) => api.post('/inner/v1/fiat-payment/test/execute', {
    testType: 'ACCOUNT_LIST',
    channelCode: '',
    businessPartner: 'DEMO_ACCOUNT',
    currency: '',
    env: getEnvByHostname(),
    executor: localStorage.getItem('username') || 'admin',
    requestBody: JSON.stringify({ externalUserId, requestId: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` })
  }),
  getUserCards: (externalUserId) => api.post('/inner/v1/fiat-payment/test/execute', {
    testType: 'CARD_LIST',
    channelCode: '',
    businessPartner: 'DEMO_ACCOUNT',
    currency: '',
    env: getEnvByHostname(),
    executor: localStorage.getItem('username') || 'admin',
    requestBody: JSON.stringify({ externalUserId, requestId: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` })
  }),
};

export const metaApi = {
  getOverview: () => api.get('/inner/v1/fiat-payment/meta'),
  getByApiCode: (apiCode) => api.get(`/inner/v1/fiat-payment/meta/${apiCode}`),
  get: (apiCode, channelCode) => api.get(`/inner/v1/fiat-payment/meta/${apiCode}/${channelCode}`),
  // 管理接口
  adminList: () => api.get('/inner/v1/fiat-payment/meta/admin/list'),
  adminGetById: (id) => api.get(`/inner/v1/fiat-payment/meta/admin/${id}`),
  adminCreate: (data) => api.post('/inner/v1/fiat-payment/meta/admin', data),
  adminUpdate: (id, data) => api.put(`/inner/v1/fiat-payment/meta/admin/${id}`, data),
  adminDelete: (id) => api.delete(`/inner/v1/fiat-payment/meta/admin/${id}`),
  adminRefresh: () => api.post('/inner/v1/fiat-payment/meta/admin/refresh'),
  getChannelConfigs: () => api.get('/inner/v1/fiat-payment/meta/channel-configs'),
  getPayoutRequirements: (channelCode, country, currency) =>
    api.get('/inner/v1/fiat-payment/meta/payout/requirements', { params: { channelCode, country, currency } }),
};

export const userApi = {
  secureLogin: (data) => api.post('/api/secure-login', data),
  logout: () => api.post('/api/logout'),
  getPublicKey: () => api.get('/api/public-key'),
  getCaptcha: () => api.get('/api/captcha'),
  getCaptchaImage: (key) => `/api/captcha/image?key=${key}`,
  getMe: () => api.get('/api/users/me'),
  getUsers: () => api.get('/api/users'),
  createUser: (data) => api.post('/api/users', data),
  updateUser: (userId, data) => api.put(`/api/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/api/users/${userId}`),
  activateUser: (userId) => api.put(`/api/users/${userId}/activate`),
  getChannelCurrencies: () => testApi.getChannelCurrencies(),
  enable2FA: (userId) => api.post(`/api/users/${userId}/2fa/enable`),
  verify2FA: (userId, code) => api.post(`/api/users/${userId}/2fa/verify`, { code }),
  disable2FA: (userId, code) => api.post(`/api/users/${userId}/2fa/disable`, { code }),
  verify2FALogin: (tempToken, code) => api.post('/api/verify-2fa', { tempToken, code }),
};

export const mockRuleApi = {
  list: (channel) => api.get('/inner/v1/fiat-payment/mock-rules', { params: channel ? { channel } : {} }),
  get: (id) => api.get(`/inner/v1/fiat-payment/mock-rules/${id}`),
  create: (data) => api.post('/inner/v1/fiat-payment/mock-rules', data),
  update: (id, data) => api.put(`/inner/v1/fiat-payment/mock-rules/${id}`, data),
  delete: (id) => api.delete(`/inner/v1/fiat-payment/mock-rules/${id}`),
  toggle: (id) => api.patch(`/inner/v1/fiat-payment/mock-rules/${id}/toggle`),
  refreshCache: (channel) => api.post('/inner/v1/fiat-payment/mock-rules/cache/refresh', null, { params: channel ? { channel } : {} }),
};
